import React, { useState, useEffect, useCallback } from 'react';
// PropTypes
import PropTypes from 'prop-types';
// Third-party libraries
import { CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { startBreakThunk, endBreakThunk } from '../../../redux/shifts/shiftsThunks';

// Assets
import circleBlurred from '../../../assets/logo/circleBlurred.png';
import Spinner from '../../../assets/svg/spinner';

// Local components and styles
import { SpinnerContainer } from '../../../pages/Dashboard/Dashboard.styles';
import {
  StatusContainer,
  TempoLogo,
  StartButton,
  EndButton,
  ViewShiftButton,
  LogoContainer,
  BreakButton,
  WelcomeText,
  StatusText,
} from './ShiftStatus.styles';
import BreakOptionsModal from './components/BreakOptionsModal';
import EndBreakConfirmationModal from './components/EndBreakConfirmationModal';

// Utils and hooks
import { getEmployeeShiftStatusMessage } from '../../../utils/helpers/dashboard/dashboardHelpers';
import { useShiftStatus } from '../../../utils/Hooks/shifts/useShiftStatus';
import { useShiftClockInManagement } from '../../../utils/Hooks/shifts/useShiftClockInManagement';
import { selectShiftStatus, selectBreakActionStatus } from '../../../redux/shifts/shiftsSlice';
import { LOADING_STATES } from '../../../utils/constants/loadingStates';
import { fetchBreaksByShiftId } from '../../../redux/breaks/breaksThunks';
import { showErrorToast } from '../../../utils/toastUtils';
import { BREAK_ACTIONS, BREAK_TYPES } from '../../../utils/constants/breakConstants';
import { getLegalEndTime } from '../../../utils/helpers/breaks/breakHelpers';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import { useTimeFormatters } from '../../../utils/Hooks/venue/useVenueTimeFormatters';
// Function to format message with venue time
const formatMessageWithVenueTime = (message, deadlineTime, formatTime) => {
  if (!message.includes('{DEADLINE_TIME}') || !deadlineTime) {
    return message;
  }

  try {
    const venueTime = formatTime(deadlineTime);
    return message.replace('{DEADLINE_TIME}', venueTime);
  } catch (error) {
    console.error('Error formatting venue time:', error);
    // Fallback to original message without time replacement
    return message.replace('{DEADLINE_TIME}', 'the scheduled time');
  }
};

const ShiftStatus = ({ employeeName, employeeShifts, onViewShift, employeeId }) => {
  const { formatTime } = useTimeFormatters();
  const [breakModalOpen, setBreakModalOpen] = useState(false);
  const [endBreakConfirmationModalOpen, setEndBreakConfirmationModalOpen] = useState(false);
  const [backendMessage, setBackendMessage] = useState('');
  const [isUserActionLoading, setIsUserActionLoading] = useState(false);
  const [isWaiveBreakLoading, setIsWaiveBreakLoading] = useState(false);
  const [legalEndTime, setLegalEndTime] = useState(null);
  const [pendingBreakAction, setPendingBreakAction] = useState(null);
  const [isBreakValidationLoading, setIsBreakValidationLoading] = useState(false);

  const changeClockStatus = useSelector((state) => state.newShifts.changeClockStatus);
  const breakActionStatus = useSelector(selectBreakActionStatus);
  const shiftStatus = useSelector(selectShiftStatus);
  const venue = useSelector(selectCurrentVenue);
  const venueId = venue?.id;

  // Get the shift status, relevant shift, and button visibility
  const {
    showStartButton,
    showEndButton,
    showBreakButton,
    showEndMealBreakButton,
    showRestButton,
    showEndRestBreakButton,
    relevantShift,
    currentBreak,
  } = useShiftStatus(employeeShifts);

  // Get the functions for clock in and clock out and the error state
  const dispatch = useDispatch();
  const { handleStartShift, handleEndShift, handleWaiveBreak } = useShiftClockInManagement();

  const isClockButtonLoading = changeClockStatus === LOADING_STATES.LOADING;
  const isBreakButtonLoading = breakActionStatus === LOADING_STATES.LOADING;

  // Function for user-triggered message updates
  const updateMessageForUserAction = useCallback(async () => {
    setIsUserActionLoading(true);
    setBackendMessage('loading status...');
    try {
      const result = await getEmployeeShiftStatusMessage(employeeId);
      if (result?.message) {
        const formattedMessage = formatMessageWithVenueTime(result.message, result.deadlineTime, formatTime);
        setBackendMessage(formattedMessage);
      } else {
        setBackendMessage('there was an error fetching your status message. Please refresh the page.');
      }
    } catch (err) {
      console.error('Error fetching backend message:', err);
      showErrorToast('Failed to load status message. Please refresh the page.');
      setBackendMessage('there was an error fetching your status message. Please refresh the page.');
    } finally {
      setIsUserActionLoading(false);
    }
  }, [employeeId, formatTime]);

  // Fetch the shift status message on component mount and every 60 seconds while the component is visible
  useEffect(() => {
    if (!employeeId) return;

    // Add a flag to track if the component is still mounted
    let isMounted = true;

    const fetchBackendMessage = async () => {
      // Check if component is still mounted before making the API call
      if (!isMounted || !employeeId) return;

      try {
        const result = await getEmployeeShiftStatusMessage(employeeId);

        // Check again if component is still mounted before updating state
        if (!isMounted) return;

        if (result?.message) {
          const formattedMessage = formatMessageWithVenueTime(result.message, result.deadlineTime, formatTime);
          setBackendMessage(formattedMessage);
        } else {
          setBackendMessage('No shifts scheduled.');
        }
      } catch (err) {
        // Only show error and update state if component is still mounted
        if (!isMounted) return;

        console.error('Error fetching backend message:', err);
        showErrorToast('Failed to load status message');
        // Still set a fallback message so the UI isn't empty
        setBackendMessage("it looks like you don't have any shifts scheduled.");
      }
    };

    // Initial fetch with loading state
    setBackendMessage('loading status...');
    fetchBackendMessage();

    // Set up polling to refresh message every minute when component is visible
    let intervalId;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Start polling when tab becomes visible
        intervalId = setInterval(() => {
          // Check if component is still mounted before making API call
          if (isMounted && employeeId) {
            fetchBackendMessage();
          }
        }, 60000); // 60 seconds
      } else {
        // Stop polling when tab is hidden
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Start polling if page is currently visible
    if (document.visibilityState === 'visible') {
      intervalId = setInterval(() => {
        // Check if component is still mounted before making API call
        if (isMounted && employeeId) {
          fetchBackendMessage();
        }
      }, 60000);
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      isMounted = false; // Set flag to prevent state updates
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [employeeId, formatTime]);

  const handleViewShift = () => {
    if (relevantShift) {
      onViewShift(relevantShift);
    }
  };

  const handleBreakClick = () => {
    setBreakModalOpen(true);
  };

  const handleBreakModalClose = () => {
    setBreakModalOpen(false);
  };

  const handleEndBreakConfirmationModalClose = () => {
    setEndBreakConfirmationModalOpen(false);
    setPendingBreakAction(null);
  };

  const handleFetchBreaks = async (shiftId) => {
    return await dispatch(fetchBreaksByShiftId(shiftId));
  };

  const handleWaiverSubmit = async (breakItem, reason) => {
    if (!relevantShift || !breakItem) return;

    try {
      setIsWaiveBreakLoading(true);
      await handleWaiveBreak(relevantShift, breakItem, reason);
      handleBreakModalClose();
      setIsWaiveBreakLoading(false);
      // Refresh status message with loading state for user action
      updateMessageForUserAction();
    } catch (err) {
      console.error('Error waiving break:', err);
      showErrorToast('Failed to waive break');
      setIsWaiveBreakLoading(false);
      // Error is already set in handleWaiveBreak
    }
  };

  // Wrapper functions for shift actions that also update the status message
  const handleStartShiftWithMessageUpdate = async (shift) => {
    try {
      await handleStartShift(shift);
      // Refresh status message with loading state for user action
      updateMessageForUserAction();
    } catch (err) {
      console.error('Error starting shift:', err);
      showErrorToast('Failed to start shift');
    }
  };

  const handleEndShiftWithMessageUpdate = async (shift) => {
    try {
      await handleEndShift(shift);
      // Refresh status message with loading state for user action
      updateMessageForUserAction();
    } catch (err) {
      console.error('Error ending shift:', err);
      showErrorToast('Failed to end shift');
    }
  };

  const handleBreakActionClick = async (action) => {
    const now = new Date();
    if (action === BREAK_ACTIONS.END_REST || action === BREAK_ACTIONS.END_MEAL) {
      if (!currentBreak) {
        console.warn('handleBreakActionClick: No current break available');
        return;
      }

      setIsBreakValidationLoading(true);
      const legalEndTimeForVenue = await getLegalEndTime(currentBreak, venueId);
      if (legalEndTimeForVenue > now) {
        setLegalEndTime(legalEndTimeForVenue);
        setPendingBreakAction(action);
        setEndBreakConfirmationModalOpen(true);
        setIsBreakValidationLoading(false);
      } else {
        await handleBreakAction(action);
        handleBreakModalClose();
        setIsBreakValidationLoading(false);
      }
    } else {
      await handleBreakAction(action);
      handleBreakModalClose();
    }
  };

  const handleBreakAction = useCallback(
    async (action) => {
      handleBreakModalClose();

      if (!relevantShift) return;

      let breakType = null;

      if (action === BREAK_ACTIONS.START_REST || action === BREAK_ACTIONS.START_MEAL) {
        breakType = action === BREAK_ACTIONS.START_REST ? BREAK_TYPES.REST : BREAK_TYPES.MEAL;

        try {
          // Using Redux thunk to start break
          await dispatch(startBreakThunk({ shiftId: relevantShift.id, breakType }));
          // Refresh status message with loading state for user action
          updateMessageForUserAction();
        } catch (err) {
          console.error('Error starting break:', err);
          showErrorToast('Failed to start break');
        }

        return;
      }

      if ((action === BREAK_ACTIONS.END_REST || action === BREAK_ACTIONS.END_MEAL) && currentBreak?.id) {
        try {
          // Using Redux thunk to end break
          await dispatch(endBreakThunk({ shiftId: relevantShift.id, breakId: currentBreak.id }));

          // Refresh status message with loading state for user action
          updateMessageForUserAction();
        } catch (err) {
          console.error('Error ending break:', err);
          showErrorToast('Failed to end break');
        }
        return;
      }
    },
    [relevantShift, currentBreak, dispatch, updateMessageForUserAction]
  );

  // Show spinner when shifts are loading
  if (shiftStatus === LOADING_STATES.LOADING) {
    return (
      <StatusContainer>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </StatusContainer>
    );
  }

  // Show modal for starting breaks, direct buttons for ending breaks
  const showBreakModal = showBreakButton || showRestButton;

  return (
    <StatusContainer>
      <LogoContainer>
        <TempoLogo src={circleBlurred} alt="Tempo Logo" />
      </LogoContainer>

      <WelcomeText variant="h5">Hey {employeeName},</WelcomeText>
      <StatusText variant="h5">{backendMessage}</StatusText>

      {showStartButton && (
        <StartButton
          variant="contained"
          onClick={() => handleStartShiftWithMessageUpdate(relevantShift)}
          disabled={isClockButtonLoading || isUserActionLoading}
        >
          {isClockButtonLoading || isUserActionLoading ? <CircularProgress color="inherit" size={16} /> : 'Start Shift'}
        </StartButton>
      )}

      {/* Direct End Break Buttons - Show when on break */}
      {showEndRestBreakButton && (
        <EndButton
          variant="contained"
          onClick={() => handleBreakActionClick(BREAK_ACTIONS.END_REST)}
          disabled={isBreakButtonLoading || isUserActionLoading || isBreakValidationLoading}
        >
          {isBreakButtonLoading || isUserActionLoading || isBreakValidationLoading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            'End Rest Break'
          )}
        </EndButton>
      )}

      {showEndMealBreakButton && (
        <EndButton
          variant="contained"
          onClick={() => handleBreakActionClick(BREAK_ACTIONS.END_MEAL)}
          disabled={isBreakButtonLoading || isUserActionLoading || isBreakValidationLoading}
        >
          {isBreakButtonLoading || isUserActionLoading || isBreakValidationLoading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            'End Meal Break'
          )}
        </EndButton>
      )}

      {/* Break Actions Modal - Show when can start breaks */}
      {showBreakModal && (
        <>
          <BreakButton
            variant="contained"
            onClick={handleBreakClick}
            disabled={isBreakButtonLoading || isUserActionLoading}
          >
            {isBreakButtonLoading || isUserActionLoading ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              'Break Actions'
            )}
          </BreakButton>
          <BreakOptionsModal
            open={breakModalOpen}
            onClose={handleBreakModalClose}
            shiftId={relevantShift?.id}
            fetchBreaks={handleFetchBreaks}
            onBreakAction={handleBreakAction}
            onWaiveSubmit={handleWaiverSubmit}
            isLoading={isBreakButtonLoading || isUserActionLoading || isWaiveBreakLoading}
            showRestButton={showRestButton}
            showBreakButton={showBreakButton}
            currentBreak={currentBreak}
          />
        </>
      )}

      {endBreakConfirmationModalOpen && pendingBreakAction && currentBreak && legalEndTime && (
        <EndBreakConfirmationModal
          open={endBreakConfirmationModalOpen}
          onClose={handleEndBreakConfirmationModalClose}
          legalEndBreakAction={pendingBreakAction}
          legalEndTime={legalEndTime}
          isLoading={isBreakButtonLoading || isUserActionLoading}
          onEndBreakSubmit={async () => {
            await handleBreakAction(pendingBreakAction);
            handleEndBreakConfirmationModalClose();
          }}
        />
      )}

      {showEndButton && (
        <EndButton
          variant="contained"
          onClick={() => handleEndShiftWithMessageUpdate(relevantShift)}
          disabled={isClockButtonLoading || isUserActionLoading}
        >
          {isClockButtonLoading || isUserActionLoading ? <CircularProgress color="inherit" size={16} /> : 'End Shift'}
        </EndButton>
      )}

      {relevantShift && (
        <ViewShiftButton variant="contained" onClick={handleViewShift}>
          View shift
        </ViewShiftButton>
      )}
    </StatusContainer>
  );
};

ShiftStatus.propTypes = {
  employeeName: PropTypes.string.isRequired,
  employeeShifts: PropTypes.array.isRequired,
  onViewShift: PropTypes.func.isRequired,
  employeeId: PropTypes.string.isRequired,
};

export default ShiftStatus;
